/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { queryTurbineInspections } from "src/services/AmplifyQuery";
import { Link } from "react-router-dom";
import {
  PrinterIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
} from "@heroicons/react/20/solid";

import Layout from "src/components/layouts/Layout.react";
import Card from "src/components/cards/Card.react";
import {
  Table,
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/InspectionTable.react";
import LoadingCard from "src/components/loading/LoadingCard.react";

/**
 * The Inspection Page component
 *
 * @author Thais Watanabe
 */
const Inspection = () => {
  const location = useLocation();

  const { inspection, lastInspectionsIds } = location.state;

  const [turbineInspections, setTurbineInspections] = useState([]);
  const [lastTurbineInspections, setLastTurbineInspections] = useState([]);

  /**
   * useEffect
   */
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  /**
   * useEffect
   */
  useEffect(() => {
    fetchTurbineInspections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchTurbineInspections
   */
  async function fetchTurbineInspections() {
    const filter = { inspectionID: { eq: inspection.id } };

    queryTurbineInspections(filter, null, [], setTurbineInspections);
  }

  /**
   * useEffect
   */
  useEffect(() => {
    fetchLastTurbineInspections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * findLastInspectionTurbines
   * @param {*} windTurbineId
   * @returns
   */
  function findLastInspectionTurbines(windTurbineId) {
    return lastTurbineInspections
      .filter((obj) => obj.windTurbineID === windTurbineId)
      .sort((a, b) => (a.date > b.date ? 1 : -1));
    // .map((obj) => obj.id);
  }

  /**
   * fetchTurbineInspections
   */
  async function fetchLastTurbineInspections() {
    const inspectionIds = lastInspectionsIds.filter((item) => item);

    if (inspectionIds.length > 0) {
      const filter = { or: [] };

      inspectionIds.forEach((id) =>
        filter.or.push({ inspectionID: { eq: id } })
      );

      queryTurbineInspections(filter, null, [], setLastTurbineInspections);
    }
  }

  return (
    <Layout
      title="登録済点検一覧（号機）"
      breadcrumbs={["登録済点検一覧", "登録済点検一覧（号機）"]}
    >
      <Card>
        <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
            <div className="ml-4 mt-2">
              <h3 className="text-md leading-6 font-medium text-gray-900">
                {inspection.windFarm.name} ・ {inspection.name}
              </h3>
            </div>
            <div className="ml-4 mt-2 flex-shrink-0">
              <Link
                to="/special"
                state={{
                  inspection: inspection,
                  turbineInspections: turbineInspections,
                }}
                className="inline-flex items-center rounded-md border border-transparent bg-teal-100 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <DocumentTextIcon
                  className="-ml-0.5 mr-2 h-4 w-4"
                  aria-hidden="true"
                />
                特記事項
              </Link>
              <Link
                to="/print"
                state={{
                  inspection: inspection,
                  lastInspectionsIds: lastInspectionsIds,
                }}
                className="ml-2 inline-flex items-center rounded-md border border-transparent bg-teal-100 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <PrinterIcon
                  className="-ml-0.5 mr-2 h-4 w-4"
                  aria-hidden="true"
                />
                全号機結果
              </Link>
              <Link
                to="/progress"
                state={{
                  inspection: inspection,
                }}
                className="ml-2 inline-flex items-center rounded-md border border-transparent bg-teal-100 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <ClipboardDocumentListIcon
                  className="-ml-0.5 mr-2 h-4 w-4"
                  aria-hidden="true"
                />
                進捗一覧表
              </Link>
              <Link
                to="/tools"
                state={{
                  inspection: inspection,
                  turbineInspections: turbineInspections
                }}
                className="ml-2 inline-flex items-center rounded-md border border-transparent bg-teal-100 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <ClipboardDocumentListIcon
                  className="-ml-0.5 mr-2 h-4 w-4"
                  aria-hidden="true"
                />
                使用工具一覧表
              </Link>
            </div>
          </div>
        </div>
      </Card>

      {!turbineInspections.length > 0 && <LoadingCard />}
      {turbineInspections.length > 0 && (
        <Card className="table-turbine-inspections">
          <Table>
            <thead>
              <tr>
                <ColHeader>作成日</ColHeader>
                <ColHeader>点検名</ColHeader>
                <ColHeader>号機</ColHeader>
                <ColHeader>発電所</ColHeader>
                <ColHeader>作成者</ColHeader>
                <ColHeader></ColHeader>
              </tr>
            </thead>
            <TableBody>
              {turbineInspections
                .sort((a, b) =>
                  a["name"].localeCompare(b["name"], undefined, {
                    numeric: true,
                    sensitivity: "base",
                  })
                )
                .map((turbineInspection, i) => (
                  <tr key={i}>
                    <Col>
                      {moment(turbineInspection.createdAt).format("YYYY-MM-DD")}
                    </Col>
                    <Col className="font-medium text-gray-900">
                      {turbineInspection.name}
                    </Col>
                    <Col>
                      <span className="inline-flex items-center px-3 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                        {turbineInspection.windTurbine.tower}号機
                      </span>
                    </Col>
                    <Col>{inspection.windFarm.name}</Col>
                    <Col>{turbineInspection.author}</Col>
                    <Col>
                      <Link
                        to="/report"
                        state={{
                          inspection: inspection,
                          turbineInspection: turbineInspection,
                          lastTurbineInspections: findLastInspectionTurbines(
                            turbineInspection.windTurbineID
                          ),
                        }}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        結果を見る
                      </Link>
                    </Col>
                  </tr>
                ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Layout>
  );
};

export default Inspection;
